"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FakeAccountsClient = void 0;
const BoclipsApiErrorFactory_1 = require("../../../test-support/BoclipsApiErrorFactory");
const PageableFactory_1 = require("../../common/model/PageableFactory");
const Account_1 = require("../model/Account");
const SortAccountBy_1 = require("../model/SortAccountBy");
const LinkEntity_1 = require("../../common/model/LinkEntity");
const AccountsFactory_1 = require("../../../test-support/AccountsFactory");
class FakeAccountsClient {
    constructor(usersClient) {
        this.accountPrices = {};
        this.accounts = [];
        this.accountApiClients = {};
        this.accountToContentPackages = {};
        this.contentPackages = [];
        this.similarAccounts = {};
        this.usersClient = usersClient;
    }
    insertAccount(account) {
        this.accounts.push(account);
    }
    insertAccountPrices(accountPrices, accountId) {
        this.accountPrices[accountId] = accountPrices;
    }
    insertAccountApiClient(accountId, apiClient) {
        this.accountApiClients[accountId] = apiClient;
    }
    setSimilarAccounts(accountId, similarAccountIds) {
        this.similarAccounts[accountId] = similarAccountIds;
    }
    async getAccounts(request) {
        const { sortBy, page = 0, size = 20 } = request || {};
        const sortedAccounts = this.accounts;
        const sortFieldsAlphabetically = (a, b) => {
            if (a < b) {
                return -1;
            }
            if (a > b) {
                return 1;
            }
            return 0;
        };
        const getSortedAccounts = () => {
            switch (sortBy) {
                case SortAccountBy_1.SortAccountBy.NAME_ASC:
                    return sortedAccounts.sort((a, b) => sortFieldsAlphabetically(a.name, b.name));
                case SortAccountBy_1.SortAccountBy.NAME_DESC:
                    return sortedAccounts
                        .sort((a, b) => sortFieldsAlphabetically(a.name, b.name))
                        .reverse();
                case SortAccountBy_1.SortAccountBy.MANAGER_ASC:
                    return sortedAccounts.sort((a, b) => sortFieldsAlphabetically(a.manager, b.manager));
                case SortAccountBy_1.SortAccountBy.MANAGER_DESC:
                    return sortedAccounts
                        .sort((a, b) => sortFieldsAlphabetically(a.manager, b.manager))
                        .reverse();
                case SortAccountBy_1.SortAccountBy.STATUS_ASC:
                    return sortedAccounts.sort((a, b) => sortFieldsAlphabetically(a.status, b.status));
                case SortAccountBy_1.SortAccountBy.STATUS_DESC:
                    return sortedAccounts
                        .sort((a, b) => sortFieldsAlphabetically(a.status, b.status))
                        .reverse();
                case SortAccountBy_1.SortAccountBy.PRODUCTS_ASC:
                    return sortedAccounts.sort((a, b) => sortFieldsAlphabetically(a.products.toString(), b.products.toString()));
                case SortAccountBy_1.SortAccountBy.PRODUCTS_DESC:
                    return sortedAccounts
                        .sort((a, b) => sortFieldsAlphabetically(a.products.toString(), b.products.toString()))
                        .reverse();
                default:
                    return sortedAccounts.sort((a, b) => sortFieldsAlphabetically(a.id, b.id));
            }
        };
        const users = await this.usersClient.getAllUsers();
        const pageOfAccounts = getSortedAccounts()
            .filter((account) => {
            return this.filterByQuery(request, account, users);
        })
            .filter((account) => {
            return this.filterByProduct(request, account);
        })
            .filter((account) => {
            return this.filterByManager(request, account);
        })
            .slice(size * page, size * (page + 1));
        const pageable = PageableFactory_1.PageableFactory.sample(pageOfAccounts, {
            size: size,
            number: page,
            totalElements: sortedAccounts.length,
            totalPages: Math.ceil(sortedAccounts.length / size),
        });
        return Promise.resolve(pageable);
    }
    getAccount(id) {
        const requestedAccount = this.accounts.filter((account) => account.id === id)[0];
        if (requestedAccount) {
            return Promise.resolve(requestedAccount);
        }
        else {
            return Promise.resolve(AccountsFactory_1.AccountsFactory.sample({ id: id }));
        }
    }
    createAccount(accountRequest) {
        if (this.accountNameIsUnique(accountRequest.name)) {
            return Promise.reject(BoclipsApiErrorFactory_1.BoclipsApiErrorFactory.sample({
                status: 409,
                message: 'Account already exists',
            }));
        }
        else {
            this.insertAccount({
                name: accountRequest.name,
                id: '611f7fc81899a0a408a3e031',
                products: [Account_1.Product.LIBRARY],
                status: Account_1.AccountStatus[accountRequest.status],
                manager: accountRequest.manager,
                marketSegment: accountRequest.marketSegment,
                deliveryMethod: accountRequest.deliveryMethod,
                defaultLicenseDurationYears: accountRequest.defaultLicenseDurationYears,
                currency: accountRequest.currency,
                type: Account_1.AccountType[accountRequest.type],
                totalActiveUsers: 0,
                accessExpiresOn: accountRequest.accessExpiresOn,
                marketingInformation: accountRequest.marketingInformation,
            });
            return Promise.resolve(this.accounts.filter((account) => account.name === accountRequest.name)[0]);
        }
    }
    deleteAccount(id) {
        const indexOfAccount = this.accounts.findIndex((account) => account.id === id);
        this.accounts.splice(indexOfAccount, 1);
        return;
    }
    async updateAccount(id, updateRequest) {
        const account = this.accounts.find((account) => account.id === id);
        if (!account) {
            return Promise.reject(BoclipsApiErrorFactory_1.BoclipsApiErrorFactory.sample({
                status: 403,
                message: 'Account not found',
            }));
        }
        if (updateRequest.name && this.accountNameIsUnique(updateRequest.name)) {
            return Promise.reject(BoclipsApiErrorFactory_1.BoclipsApiErrorFactory.sample({
                status: 409,
                message: 'Account already exists',
            }));
        }
        const indexOfAccount = this.accounts.findIndex((account) => account.id === id);
        const accountToInsert = {
            ...account,
            name: updateRequest.name || account.name,
            manager: updateRequest.manager || account.manager,
            status: updateRequest.status || account.status,
            type: updateRequest.type || account.type,
            deliveryMethod: updateRequest.deliveryMethod || account.deliveryMethod,
            currency: updateRequest.currency || account.currency,
            accessExpiresOn: updateRequest.accessExpiresOn?.date || account.accessExpiresOn,
            defaultLicenseDurationYears: updateRequest.defaultLicenseDurationYears ??
                account.defaultLicenseDurationYears,
            marketingInformation: updateRequest.companySegments
                ? {
                    ...account.marketingInformation,
                    companySegments: updateRequest.companySegments,
                }
                : account.marketingInformation,
        };
        this.accounts.splice(indexOfAccount, 1, accountToInsert);
        const usersToUpdate = await this.usersClient.getAllUsers();
        const canOrder = updateRequest.status === Account_1.AccountStatus.ACTIVE;
        usersToUpdate.map((user) => this.usersClient.setPermissionsOfUser(user.id, {
            canOrder: canOrder,
            canManageUsers: false,
        }));
        const updatedAccount = this.accounts.find((account) => account.id === id);
        return Promise.resolve(updatedAccount);
    }
    async getAccountUsers(request) {
        const { page = 0, size = 25 } = request;
        let users = await this.usersClient.getAllUsers();
        if (request.emailQuery) {
            users = users.filter((user) => {
                return user.email
                    .toLowerCase()
                    .includes(request.emailQuery.toLowerCase().trim());
            });
        }
        const pageOfUsers = users
            .map((it) => ({
            id: it.id,
            email: it.email,
            firstName: it.firstName,
            lastName: it.lastName,
            permissions: this.usersClient.getPermissionsOfUser(it.id),
            accessExpiresOn: it.accessExpiresOn,
            links: {
                sendActivationEmail: new LinkEntity_1.Link({
                    href: 'http://example.com',
                }),
            },
        }))
            .slice(size * page, size * (page + 1));
        const pageable = PageableFactory_1.PageableFactory.sample(pageOfUsers, {
            size: size,
            number: page,
            totalElements: users.length,
            totalPages: Math.ceil(users.length / size),
        });
        return Promise.resolve(pageable);
    }
    async getAccountBulkUsers(id, request) {
        const users = await this.usersClient.getAllUsers();
        return Promise.resolve(users
            .filter((user) => {
            return user.account?.id === id && request.userIds.includes(user.id);
        })
            .map((user) => {
            return {
                id: user.id,
                email: user.email,
                firstName: user.firstName,
                lastName: user.lastName,
            };
        }));
    }
    insertContentPackage(contentPackage) {
        this.contentPackages.push(contentPackage);
    }
    getAccountContentPackages(id) {
        const contentPackages = this.accountToContentPackages[id];
        return Promise.resolve(contentPackages || []);
    }
    updateAccountContentPackages(id, updateAccountContentPackages) {
        const contentPackage = this.contentPackages.find((contentPackage) => contentPackage.id === updateAccountContentPackages.contentPackageId);
        if (contentPackage) {
            this.accountToContentPackages[id] = [
                {
                    id: updateAccountContentPackages.contentPackageId,
                    name: contentPackage.name,
                },
            ];
            return Promise.resolve();
        }
        else {
            return Promise.reject(BoclipsApiErrorFactory_1.BoclipsApiErrorFactory.sample({
                status: 404,
                message: 'Content package not found',
            }));
        }
    }
    async getAccountPrices(id) {
        const prices = this.accountPrices[id];
        if (prices) {
            return Promise.resolve(prices);
        }
        return Promise.reject('Prices not found for account with id: ' + id);
    }
    async clear() {
        this.accounts = [];
        this.accountPrices = {};
        this.accountApiClients = {};
        const allUsers = await this.usersClient.getAllUsers();
        allUsers
            .filter((user) => user.account?.id !== null)
            .map((userToDelete) => this.usersClient.deleteUser(userToDelete.id));
    }
    updateUserAccount(user, updateRequest) {
        const id = user.account?.id;
        if (!id) {
            throw Error('Not possible to not have account.id in real life!');
        }
        return this.updateAccount(id, updateRequest);
    }
    getAccountApiClient(id) {
        const apiClient = this.accountApiClients[id];
        if (apiClient) {
            return Promise.resolve(apiClient);
        }
        return Promise.reject('Api client not found for account with id: ' + id);
    }
    getSimilarAccounts(id) {
        const similarAccountIds = this.similarAccounts[id];
        const similarAccounts = this.accounts.filter((account) => similarAccountIds.some((accountId) => account.id === accountId));
        return Promise.resolve(similarAccounts);
    }
    filterByQuery(request, account, users) {
        if (request?.query) {
            const nameMatches = account.name.toLowerCase().indexOf(request.query.toLowerCase()) > -1;
            const emailMatches = users.some((user) => user.account?.id === account.id && user.email === request.query);
            return nameMatches || emailMatches;
        }
        else {
            return true;
        }
    }
    filterByProduct(request, account) {
        if (request?.product) {
            return account.products
                .map((p) => p.valueOf())
                .some((product) => request.product?.includes(product));
        }
        else {
            return true;
        }
    }
    filterByManager(request, account) {
        if (request?.manager) {
            return request.manager.includes(account.manager);
        }
        else {
            return true;
        }
    }
    accountNameIsUnique(accountName) {
        return (this.accounts?.filter((account) => account.name === accountName).length >
            0);
    }
}
exports.FakeAccountsClient = FakeAccountsClient;
//# sourceMappingURL=FakeAccountsClient.js.map